import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';

// eslint-disable-next-line import/no-cycle
import { ReadwiseFetchServerError } from '../../utils/Errors';
import { PromptModel } from './constants';
import { CreatedPrompts, OverriddenPrompts, Prompts } from './types';

/**
 * Normalize the default and user-customized prompts into a standard format,
 * and index them to make them accessible for application code.
 *
 * Default prompts are always in V2 format (fetched from backend V2 endpoint).
 *
 * @param defaultPrompts
 * @param overriddenPrompts
 * @param createdPrompts
 */
export function useTransformedPrompts(
  defaultPrompts: Prompts,
  overriddenPrompts?: OverriddenPrompts,
  createdPrompts?: CreatedPrompts,
) {
  const normalizedDefaultPrompts = cloneDeep(defaultPrompts);
  const normalizedOverriddenPrompts = overriddenPrompts ? cloneDeep(overriddenPrompts) : undefined;

  // merge overridden prompts
  if (normalizedOverriddenPrompts) {
    for (const promptScope of normalizedDefaultPrompts.data) {
      if (normalizedOverriddenPrompts[promptScope.type]) {
        for (const prompt of promptScope.prompts) {
          if (normalizedOverriddenPrompts[promptScope.type][prompt.type]) {
            assign(prompt, normalizedOverriddenPrompts[promptScope.type][prompt.type]);
            prompt.isDefaultPrompt = false;
          }
        }
      }
    }
  }

  // merge created prompts
  if (createdPrompts) {
    for (const promptScope of normalizedDefaultPrompts.data) {
      if (createdPrompts?.[promptScope.type]) {
        promptScope.prompts.push(...createdPrompts[promptScope.type]);
      }
    }
  }

  return normalizedDefaultPrompts;
}

export function getModelLabel(model: PromptModel) {
  switch (model) {
    case PromptModel.GPT_35_TURBO:
      return 'GPT-3.5 Turbo';
    case PromptModel.GPT_4:
      return 'GPT-4';
    case PromptModel.GPT_4_TURBO:
      return 'GPT-4 Turbo';
    case PromptModel.GPT_4o:
      return 'GPT-4o';
    case PromptModel.GPT_4o_MINI:
      return 'GPT-4o mini';
    case PromptModel.GPT_5:
      return 'GPT-5';
    case PromptModel.GPT_5_1:
      return 'GPT-5.1';
    case PromptModel.GPT_5_MINI:
      return 'GPT-5 mini';
    case PromptModel.GPT_5_NANO:
      return 'GPT-5 nano';
    case PromptModel.GPT_4_1:
      return 'GPT-4.1';
    case PromptModel.GPT_4_1_MINI:
      return 'GPT-4.1 mini';
    case PromptModel.GPT_4_1_NANO:
      return 'GPT-4.1 nano';
    case PromptModel.O1:
      return 'o1';
    case PromptModel.O1_MINI:
      return 'o1-mini';
    case PromptModel.O3:
      return 'o3';
    case PromptModel.O3_MINI:
      return 'o3-mini';
    case PromptModel.O4_MINI:
      return 'o4-mini';
    case PromptModel.GPT_5_2:
      return 'GPT-5.2';
    case PromptModel.GPT_5_4:
      return 'GPT-5.4';
    case PromptModel.GPT_5_4_MINI:
      return 'GPT-5.4 mini';
    case PromptModel.GPT_5_5:
      return 'GPT-5.5';
    case PromptModel.GPT_5_6_SOL:
      return 'GPT-5.6 Sol';
    case PromptModel.GPT_5_6_TERRA:
      return 'GPT-5.6 Terra';
    case PromptModel.GPT_5_6_LUNA:
      return 'GPT-5.6 Luna';
    default:
      return 'GPT-5 mini';
  }
}

export function errorIncludesResponse(error: unknown): error is ReadwiseFetchServerError {
  return isObject(error) && Object.hasOwn(error, 'response');
}

// Keep in sync with `BYOK_MODELS` in
// reader/reader_chatwebsockets/byok_models.py — that is the backend whitelist
// that resolves `byok:<EnumName>` wire ids for V2 skills and chat dropdowns.
// If you add/remove an entry here, update the backend list (and vice versa).
export const BYOK_PROMPT_MODELS: PromptModel[] = [
  PromptModel.GPT_5_6_SOL,
  PromptModel.GPT_5_6_TERRA,
  PromptModel.GPT_5_6_LUNA,
  PromptModel.GPT_5_5,
  PromptModel.GPT_5_4,
  PromptModel.GPT_5_4_MINI,
  PromptModel.GPT_5_2,
  PromptModel.GPT_5_1,
  PromptModel.GPT_5_MINI,
  PromptModel.GPT_5_NANO,
  PromptModel.GPT_4_1,
  PromptModel.O3,
  PromptModel.O4_MINI,
];

// Models every BYOK picker shows up-front; the rest collapse behind "See more".
export const BYOK_DEFAULT_VISIBLE_MODELS: PromptModel[] = [
  PromptModel.GPT_5_6_SOL,
  PromptModel.GPT_5_6_TERRA,
  PromptModel.GPT_5_6_LUNA,
  PromptModel.GPT_5_MINI,
];

// Collapse a BYOK model list to the default-visible set (plus the currently
// selected model, so users can always see what's active). `showAll` bypasses it.
export function splitByokModelsForDisplay<T>(
  items: T[],
  getModel: (item: T) => PromptModel | null,
  isSelected: (item: T) => boolean,
  showAll: boolean,
): { visible: T[]; hiddenCount: number } {
  if (showAll) {
    return { visible: items, hiddenCount: 0 };
  }
  const visible = items.filter((item) => {
    const model = getModel(item);
    return (model !== null && BYOK_DEFAULT_VISIBLE_MODELS.includes(model)) || isSelected(item);
  });
  return { visible, hiddenCount: items.length - visible.length };
}

// Wire format shared with reader/reader_chatwebsockets/byok_models.py.
export const BYOK_MODEL_PREFIX = 'byok:';

export function byokModelIdFor(model: PromptModel): string {
  return `${BYOK_MODEL_PREFIX}${model}`;
}

export function isByokModelId(modelId: string | null | undefined): boolean {
  return typeof modelId === 'string' && modelId.startsWith(BYOK_MODEL_PREFIX);
}

export function promptModelFromByokId(modelId: string): PromptModel | null {
  if (!isByokModelId(modelId)) {
    return null;
  }
  const name = modelId.slice(BYOK_MODEL_PREFIX.length);
  for (const value of Object.values(PromptModel)) {
    if (value === name) {
      return value;
    }
  }
  return null;
}
